import type { StatusEsteira } from '../api';
import { lerDataFalha } from './dataFalha';
import { ORDEM_STATUS, ROTULO_PENDENTE, ST_META } from './ordemStatus';

/**
 * VOCABULÁRIO DA VISÃO MACRO (24/09) — agrupa status para EXIBIÇÃO, sem tocar
 * na classificação.
 *
 * A macro é a visão de quem olha o dia inteiro de uma vez e precisa de duas
 * perguntas respondidas: "o que falhou?" e "o que falta?". As distinções finas
 * (erro vs. alerta, executando vs. na fila) importam no board e no nano, onde
 * se investiga um bot específico — na macro elas fragmentam a leitura.
 *
 * DUAS FUSÕES, só aqui:
 *
 * - `err` + `warn` → **"Falha de processamento"**. Medido em produção em 24/09:
 *   dos 1070 pares (empresa, bot) com erro e/ou alerta em 30 dias, 923 (86%)
 *   são SÓ alerta, 143 (13%) têm os dois e 4 são só erro. Sem a fusão, a macro
 *   mostrava a esmagadora maioria dos problemas numa pílula que parecia
 *   secundária.
 *
 * - `run` + `wait` → **"Empresas a processar"**. As duas são trabalho pendente,
 *   que é exatamente o que o círculo do nó já conta (`wait + run`) desde 24/09.
 *   Manter duas pílulas para a mesma pergunta dividia o número que o círculo
 *   apresenta unido.
 *
 * O QUE ESTE MÓDULO **NÃO** FAZ — e é o ponto que o mantém seguro:
 *
 * Ele NÃO reclassifica nada. `warn` continua sendo `warn` no dado, continua
 * contando como concluída na barra (`passedBarra = done + warn`, "warn não trava
 * a rota" — §3.2) e continua distinto no board e no nano. A decisão do dono foi
 * explícita: *"só muda o rótulo e o filtro; o % continua igual"*. Se algum dia a
 * regra do percentual mudar, ela muda em `derivarResumo`, não aqui.
 *
 * Por isso a fusão vive num módulo de VOCABULÁRIO e não em `ordemStatus`: lá é o
 * contrato de classificação que o nó, o popup e a dedup compartilham, e mexer
 * nele mudaria a conta. Aqui é só como o nome aparece na tela.
 */

/** Chave de um grupo de exibição da macro. Os não-fundidos usam o próprio status. */
export type StatusMacro = 'falha' | 'pendente' | 'done' | 'skip';

/** Para qual grupo da macro cada status canônico vai. */
export const GRUPO_MACRO: Record<StatusEsteira, StatusMacro> = {
  err: 'falha',
  warn: 'falha',
  run: 'pendente',
  wait: 'pendente',
  done: 'done',
  skip: 'skip',
};

/** Os status canônicos que compõem cada grupo — a volta do `GRUPO_MACRO`. */
export const STATUS_DO_GRUPO: Record<StatusMacro, StatusEsteira[]> = {
  falha: ['err', 'warn'],
  pendente: ['run', 'wait'],
  done: ['done'],
  skip: ['skip'],
};

/**
 * Os grupos que viram PÍLULA DE FILTRO na macro/micro.
 *
 * `pendente` (run + wait) FORA, por pedido do dono (25/09): *"na tela macro
 * retire o filtro de empresas a processar"*.
 *
 * Histórico, porque a decisão oscilou e é bom que o próximo a mexer saiba: saiu
 * na manhã de 25/09 — o círculo do nó já conta essas empresas e a barra passou a
 * mostrá-las em azul, então o toggle parecia redundante —, voltou no mesmo dia
 * por decisão do supervisor, e saiu de novo à tarde, por pedido direto.
 *
 * A constante fica (em vez de voltar ao `GRUPOS_MACRO_ORDENADOS` direto): ela é
 * o lugar declarado de "o que é filtrável", e a próxima mudança mexe aqui em vez
 * de espalhar condição pelo componente — que é justamente o que permitiu esta
 * ida e volta custar uma linha.
 */
export const GRUPOS_FILTRAVEIS_MACRO: StatusMacro[] = ['falha', 'done', 'skip'];

/**
 * Rótulo de cada grupo na macro.
 *
 * `ROTULO_PENDENTE` vem de `ordemStatus` (onde nomeia o `wait` em todas as telas)
 * e é reusado aqui para o grupo `run + wait`: ter a mesma coisa com dois nomes em
 * telas diferentes é o tipo de detalhe que faz duas pessoas discordarem sobre o
 * mesmo número.
 */
export const ROTULO_MACRO: Record<StatusMacro, string> = {
  falha: 'Falha de processamento',
  pendente: ROTULO_PENDENTE,
  done: ST_META.done.label,
  skip: ST_META.skip.label,
};

/** Tom visual de cada grupo — o do status mais severo que ele contém. */
export const TOM_DO_GRUPO_MACRO: Record<StatusMacro, StatusEsteira> = {
  falha: 'err',
  pendente: 'wait',
  done: 'done',
  skip: 'skip',
};

/**
 * O RODAPÉ do nó na macro: `⚠` some da tela e o seu número entra somado no `✕`.
 *
 * Pedido do dono (25/09): *"agora não vai ter mais o sinal de aviso, vai ser
 * apenas ✓ N · ✕ N · N empresas. Apenas para a visualização macro, e o aviso vai
 * sumir apenas visualmente, mas ele vai estar contando no ✕ junto"*.
 *
 * É a MESMA fusão que a pílula "Falha de processamento" já faz desde 24/09 —
 * `err` e `warn` são um grupo só na macro. O rodapé era o último lugar da visão
 * que ainda os separava, e um nó que dizia `✕ 61 · ⚠ 147` ao lado de uma pílula
 * que dizia "Falha de processamento: 208" apresentava o mesmo fato com duas
 * réguas. Somar aqui é o que faz os dois números fecharem.
 *
 * Vive neste módulo, e não solto na chamada do `emojiMeta`, justamente porque
 * `STATUS_DO_GRUPO.falha` é a definição de quem entra na soma: se um terceiro
 * status virar falha algum dia, ele entra aqui sem ninguém lembrar do rodapé.
 *
 * NÃO reclassifica nada, como o resto do módulo: o `warn` continua `warn` no
 * dado, continua contando como avanço na barra (`passedBarra = done + warn`) e
 * continua separado no micro e no board, onde se investiga um bot específico.
 */
export const falhasDaMacro = (contagens: Partial<Record<StatusEsteira, number>>): number =>
  STATUS_DO_GRUPO.falha.reduce((soma, st) => soma + (contagens[st] ?? 0), 0);

/**
 * O tooltip do selo "já falhou Nx" — a frase muda conforme o estado ATUAL.
 *
 * O selo aparece em qualquer estado que não seja `err` (em `err` o número já
 * está no badge e repetir seria ruído). Até 25/09 o texto era fixo em "Esta
 * empresa concluiu, mas...", e isso MENTIA na maioria dos casos: medido em
 * produção, os pares (empresa, bot) com falha anterior são 256 em `skip`
 * (Pendente) contra 115 em `ok` — a frase errada era a mais frequente. Foi o que
 * o dono estranhou ao ver "Pendente" ao lado de "já falhou 3x".
 *
 * O selo em si está certo e é útil: "pendente agora, mas já falhou 3x" é um
 * sinal diferente de uma pendente limpa — pode ser trava recorrente. Quem estava
 * errado era só o texto.
 *
 * `ultimaFalhaEm` (28/09) acrescenta QUANDO foi a última — "(última em 14/09 às
 * 04:49)". Só a data: o TEXTO da falha nunca entra aqui, porque este tooltip
 * aparece em linha concluída, e quem concluiu não mostra mensagem de erro. Sem
 * data legível (backend antigo, resposta em cache) a frase sai como sempre foi.
 */
export const tituloFalhasAnteriores = (
  falhas: number,
  estado?: StatusEsteira,
  ultimaFalhaEm?: string | null,
): string => {
  const data = lerDataFalha(ultimaFalhaEm);
  const quando = data ? ` (última em ${data.dia} às ${data.hora})` : '';
  const sufixo = `${falhas} execução(ões) falharam antes no período selecionado${quando}`;
  if (estado === 'done' || estado === 'warn') return `Esta empresa concluiu, mas ${sufixo}`;
  if (estado === 'skip') return `Esta empresa está pendente e ${sufixo}`;
  if (estado === 'run') return `Esta empresa está executando agora e ${sufixo}`;
  if (estado === 'wait') return `Esta empresa está na fila e ${sufixo}`;
  // Estado desconhecido (payload novo antes do front acompanhar): a frase
  // neutra continua verdadeira em qualquer estado, em vez de afirmar um que
  // talvez não seja o dela.
  return `${falhas} execução(ões) desta empresa falharam antes no período selecionado${quando}`;
};

/**
 * Os grupos da macro na ordem de exibição, derivada de `ORDEM_STATUS` para não
 * criar uma segunda ordem que possa divergir: cada grupo herda a posição do seu
 * status mais prioritário.
 */
export const GRUPOS_MACRO_ORDENADOS: StatusMacro[] = (
  Object.keys(STATUS_DO_GRUPO) as StatusMacro[]
).sort(
  (a, b) =>
    Math.min(...STATUS_DO_GRUPO[a].map(s => ORDEM_STATUS[s])) -
    Math.min(...STATUS_DO_GRUPO[b].map(s => ORDEM_STATUS[s])),
);
